import { Button, Input, Label, Separator } from '@playcastdotio/Shadcn';
import { PlaycastReactFunctions } from '../PlaycastWrapper';
import { useState, useRef, useEffect } from 'react';
import { RecordKey } from './RecordKey';

export const GameClips = ({ context }: { context: PlaycastReactFunctions }) => {
  const [recordSeconds, setRecordSeconds] = useState(context.stateStores.gameClipConfigState.bufferDurationSeconds);
  const [folderPath, setFolderPath] = useState(context.stateStores.gameClipConfigState.directoryPath);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up the timer when component unmounts
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setRecordSeconds(0);
      return;
    }
    let numericValue = parseInt(value, 10);
    if (isNaN(numericValue)) {
      setRecordSeconds(0);
      return;
    }
    if (numericValue < 0) numericValue = 0;
    if (numericValue > 15) numericValue = 15;

    setRecordSeconds(numericValue);
    e.target.value = numericValue.toString();

    // Clear any existing timer to prevent memory leaks
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Set a new timer to update the config after .4 seconds
    debounceTimerRef.current = setTimeout(() => {
      context.targetFunctions.updateGameClipConfig({
        ...context.stateStores.gameClipConfigState,
        bufferDurationSeconds: numericValue,
      });
      debounceTimerRef.current = null;
    }, 400);
  };

  const handleChangeFolder = async () => {
    const folderPath = await window.playcast?.openFolderDialog();
    if (folderPath) {
      setFolderPath(folderPath);
      context.targetFunctions.updateGameClipConfig({
        ...context.stateStores.gameClipConfigState,
        directoryPath: folderPath,
      });
    }
  };

  return (
    <div data-testid='content-root' className='flex flex-1 flex-col gap-4 text-white'>
      <Label className='font-[Syne] text-3xl'>Game Clips</Label>
      <div data-testid='content-children-container' className='flex h-full w-full flex-col gap-4'>
        <div className='flex flex-col space-y-4'>
          <Label className='font-[Syne] text-xl'>Hotkey</Label>
          <div className='flex items-center gap-2'>
            <span>Start/Stop Recording</span>
            <RecordKey context={context} isEditable />
          </div>
        </div>
        <Separator className='m-0 bg-white p-0' />
        <div className='mb-4 flex flex-col space-y-4'>
          <Label className='font-[Syne] text-xl'>Recording Configuration</Label>
          <div className='flex items-center'>
            Start recording{' '}
            <div className='relative flex'>
              <Input
                id='record-seconds-input'
                type='number'
                value={recordSeconds}
                min='0'
                max='15'
                onChange={handleInputChange}
                className='border-midnight-dark mx-2 max-w-12 border bg-gray-800 p-0 text-right text-lg font-bold drop-shadow-lg focus:outline-none focus:ring-0'
              />
              <span className='absolute -bottom-5 left-1/2 flex w-fit -translate-x-1/2 transform whitespace-nowrap text-xs'>(0-15)</span>
            </div>{' '}
            Seconds ago
          </div>
        </div>
        <Separator className='m-0 bg-white p-0' />
        <div className='flex items-center'>
          <div className='flex flex-col'>
            <span>Saved clips location</span>
            <span className='font-light text-gray-400'>{folderPath}</span>
          </div>
          <Button
            className='bg-primary theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] hover:bg-primary/60 focus:bg-primary active:bg-primary ml-auto text-white'
            onClick={handleChangeFolder}
          >
            Change Folder
          </Button>
        </div>
      </div>
    </div>
  );
};
